import React, { FC, useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import { Input } from '../general/forms/Input';
import { PasswordInput } from '../general/forms/PasswordInput';
import { Button } from '../general/Button';
import { Colors } from '../../styles/colors';
import { AppLogoHeader } from '../general/AppLogoHeader';
import { useShowConfirmationPopup, ConfirmationPopup } from '../../components/general/ConfirmationPopup';
import { isEmail } from '../../utils/Validators';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;

  min-height: 100%;
`;

const LoginWrapper = styled.div`
  max-width: 100vw;
  width: 375px;
  padding: 3rem 1rem;
  margin: auto;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;

  margin-top: 2rem;
`;

const ButtonWrapper = styled.div`
  margin-top: 2.5rem;
  align-self: center;
`;

const InfoText = styled.p`
  color: ${Colors.grayMid};
  margin-top: 1rem;
  font-weight: 700;
  font-size: 1.125rem;
`;

interface Props {
  appName?: string;
  onLoginClick?: (basicAuthHeader: { [key: string]: string }) => void;
  onError?: (email: string, passwordLength: number) => void;
  onForgotPasswordClick?: (email: string | null) => void;
}

export const LoginForm: FC<Props> = (props) => {
  const { appName, onLoginClick, onError } = props;
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [resetPasswordVisible, setResetPasswordVisible] = useState(false);
  const [resetEmail, setResetEmail] = useState<string>('');

  const passwordRef = useRef<HTMLInputElement | null>(null);

  const login = () => {
    if (isEmail(email) && password?.length >= 3) {
      const auth = btoa(`${email.toLowerCase()}:${password}`);
      const authHeader = {
        Authorization: `Basic ${auth}`,
      };

      // callback
      if (onLoginClick) {
        onLoginClick(authHeader);
      }
    } else {
      // callback
      if (onError) {
        onError(email.toLowerCase() || '', password?.length || 0);
      }
    }
  };

  const onEmailKeyUp = (event: any) => {
    if (event.key === 'Enter') {
      passwordRef.current?.focus();
    }
  };
  const onPasswordKeyUp = (event: any) => {
    if (event.key === 'Enter') {
      login();
    }
  };

  useEffect(() => {
    if (email) {
      setResetEmail(email.toLowerCase());
    }
  }, [email]);

  useEffect(() => {
    const emailInput = document.querySelector('input#email') as HTMLInputElement;
    emailInput?.focus();   // focus email input on startup
  }, []);

  return (
    <Container>
      <ConfirmationPopup
        config={{
          open: resetPasswordVisible,
          actionButtonLabel: 'Neues Password anfordern',
          closeFn: () => {
            setResetPasswordVisible(false);
          },
          actionFn: (close) => {
            if (props.onForgotPasswordClick) {
              props.onForgotPasswordClick(resetEmail);
            }
            close();
          },
          renderer: () => (
            <Input
              id={'reset-password-email'}
              label={'Benutzername (E-Mail Adresse)'}
              type={'email'}
              value={resetEmail}
              onChange={setResetEmail}
            />
          ),
        }}
      />
      <LoginWrapper>
        <AppLogoHeader appName={appName} />
        <InfoText>Melden Sie sich jetzt mit Ihren persönlichen Zugangsdaten an.</InfoText>

        <Form>
          <Input
            id={'email'}
            label={'Benutzername (E-Mail Adresse)'}
            type={'email'}
            value={email}
            onChange={setEmail}
            onKeyUp={onEmailKeyUp}
          />
          <PasswordInput
            id={'password'}
            label={'Passwort'}
            link={{
              text: 'Passwort vergessen?',
              actionFn: () => {
                setResetPasswordVisible(true);
              },
            }}
            value={password}
            onChange={setPassword}
            onKeyUp={onPasswordKeyUp}
            inputRef={passwordRef}
          />
          <ButtonWrapper>
            <Button
              onClick={(event: React.FormEvent<HTMLButtonElement>) => {
                event.preventDefault();
                login();
              }}
            >
              Anmelden
            </Button>
          </ButtonWrapper>
        </Form>
      </LoginWrapper>
    </Container>
  );
};
